<script setup>
import {register} from 'swiper/element/bundle';
import {nextTick, onMounted, ref, computed} from "vue";
import axios from "axios";
import { apiError } from '@functions/apiError.js';
import ApiErrorModal from '@parts/ApiErrorModal.vue';
import router from '@/router/index.js';
import { url } from '@/config/urls';
import { useAuthStore } from '@stores/auth.js';
import { modalUtil } from '@functions/modalUtil.js';
import { Capacitor } from '@capacitor/core'; 

const { showModal } = modalUtil();
// エラーメッセージの状態管理
const errorMessages = ref([]);

// ローディングフラグ
const isLoading = ref(false);

const isPc = ref(window.innerWidth >= 822);
// リサイズ対応を追加
window.addEventListener('resize', () => {
  isPc.value = window.innerWidth >= 822;
});

const advertisementList = ref([]);
const newsList = ref([]);
const supportLinkList = ref([]);
const newsApiCompleted = ref(false);

// 権限状態
const auth = useAuthStore();

// auth.userから直接取得
const user = computed(() => auth.user || {
  orderFlg: null,
  isPermittedOrder: false,
  isOrderTermsAgreed: false,
  isPermittedReturnRequest: false,
  isReturnTermsAgreed: false
})

//　広告リンク押下時
const onClickAdvertisementLink = (advertisement) =>
    // 行動履歴APIの呼び出し
    axios.post(props.actionHistoryUrl, {
      logType: 'ad',
      targetLogId: advertisement.id,
      body: advertisement.title
    })
    .catch(error => {
      apiError(error, errorMessages);
    });

//　お知らせ詳細リンク押下時
const onClickNewsDetailLink = (news) =>
    // 行動履歴APIの呼び出し
    axios.post(props.actionHistoryUrl, {
      logType: 'news',
      targetLogId: news.id,
      body: news.title
    })
    .then(() => router.push(`/news/${news.id}`))
    .catch(error => {
      apiError(error, errorMessages);
    });

// コンポーネントマウント後の処理
onMounted(() => {
  // 広告一覧取得APIの呼び出し
  getAdvertisementList();

  // お知らせ一覧取得APIの呼び出し
  axios.get(props.newsListUrl, {params: {size: 3}})
  .then(response => newsList.value = response.data.response.news)
  .catch(error => {
    apiError(error, errorMessages);
  })
  .finally(() => newsApiCompleted.value = true);

  // 顧客支援リンク一覧取得APIの呼び出し
  axios.get(props.supportLinkListUrl, {params: {size: 100}})
  .then(response => supportLinkList.value = response.data.response.supportLinks)
  .catch(error => {
    apiError(error, errorMessages);
  });
});

const useSwiper = computed(() => {
  const slideCount = advertisementList.value.length;

  // PCは3枚以上、スマホは2枚以上でSwiper
  return isPc.value
    ? slideCount >= 3
    : slideCount >= 2;
});

const getAdvertisementList = () => {
  // 広告一覧取得APIの呼び出し
  axios.get(props.advertisementListUrl, {params: {size: 100}})
    .then(response => {
      // 広告一覧がない場合は処理を終了
      if(response.data.response.totalNum === 0) return;

      advertisementList.value = response.data.response.advertisements;

      if (useSwiper.value) {
        // スライダーの描画
        nextTick(() => {
          register();
          const swiperEl = document.querySelector('#top-swiper');
            
          Object.assign(swiperEl, {
            loop: useSwiper.value,
            autoplay: useSwiper.value ? {
            delay: 5000,
            pauseOnMouseEnter: true,
            disableOnInteraction: false,
          } : false,
          speed: 500,
          navigation: {
            nextEl: ".com_carousel1 .swiper-button-next",
            prevEl: ".com_carousel1 .swiper-button-prev",
          },
          slidesPerView: 1,
          breakpoints: {
            822: {
              slidesPerView: 3
            }
          }
        });
        swiperEl.initialize();
      });
    }
  }).catch(error => {
    apiError(error, errorMessages);
  });
}

// 発注/在庫問合せボタン押下時
const onClickOrderSearch = () => {
  
  if (!user.value.isPermittedOrder) {
    // 在庫問合せTOPへ
    router.push(props.orderSearchPageUrl);
    return;
  }
  
  if (user.value.isOrderTermsAgreed) {
    // 発注TOPへ
    router.push(props.orderSearchPageUrl);
  } else {
    showModal('orderTermsModal');
  }
}

// 返品依頼ボタン押下時
const onClickReturnSearch = () => {
  if (user.value.isReturnTermsAgreed) {
    router.push(props.returnSearchPageUrl);
  } else {
    showModal('returnTermsModal');
  }
}

// ネイティブアプリ判定
const isNativeApp = Capacitor.isNativePlatform();

// コンポーネントに渡されたpropsの取得
const props = defineProps({
  isAuthenticated: Boolean,
  advertisementListUrl: {type: String, default: url.api.advertisement},
  newsListUrl: {type: String, default: url.api.news.list},
  supportLinkListUrl: {type: String, default: url.api.supportLink},
  actionHistoryUrl: {type: String, default: url.api.actionHistory},
  newsListPageUrl: {type: String, default: url.page.news.list},
  newsDetailPageUrl: {type: String, default: url.page.news.detail},
  orderResultsPageUrl: {type: String, default: url.page.orderResults},
  orderSearchPageUrl: {type: String, default: url.page.orderSearch},
  returnSearchPageUrl: {type: String, default: url.page.returnSearch},
  userDataUrl: {type: String, default: url.api.userData},
  orderTermsAgreeApiUrl: {type: String, default: url.api.orderTerms.agree},
  returnTermsAgreeApiUrl: {type: String, default: url.api.returnTerms.agree},
  loginUrl: {type: String, default: url.login},
  error: {type: String, default: url.error}
});
</script>

<template>
  <div id="contents1" class="mb1">
    <div v-if="advertisementList.length >= 1">
      <div v-if="advertisementList.length > 0 && !useSwiper" class="com_carousel1" :class="{
        'slide-1': advertisementList.length === 1,
        'slide-2': advertisementList.length === 2
      }">
        <div class="static-slides">
          <div class="static-slide" v-for="advertisement in advertisementList">
            <a :href="advertisement.url" target="_blank"
              @click="() => onClickAdvertisementLink(advertisement)">
              <img :src="advertisement.imagePath">
            </a>
          </div>
        </div>
      </div>
      <!-- 3枚以上：Swiper使用 -->
      <div v-else class="com_carousel1">
        <div class="swiper">
          <swiper-container init="false" id="top-swiper">
            <swiper-slide class="swiper-slide" v-for="advertisement in advertisementList">
              <a :href="advertisement.url" target="_blank"
                @click="() => onClickAdvertisementLink(advertisement)">
                <img :src="advertisement.imagePath">
              </a>
            </swiper-slide>
          </swiper-container>
          <div class="swiper-button-prev"></div>
          <div class="swiper-button-next"></div>
        </div>
      </div>
    </div>
    <div class="com_inner2">
      <div class="com_br_cnt1">
        <h2 class="com_title2 com_mgb5">お知らせ</h2>
        <p class="com_center1" v-if="newsApiCompleted && newsList.length === 0">現在お知らせはありません。</p>
        <table v-else class="com_d_list1">
          <tbody>
          <tr v-for="news in newsList">
            <th><span>{{ news.notificationDate }}</span>
              <div class="com_tags1">
                <span v-if="news.newFlg" class="tag1">NEW</span>
                <span v-if="news.importance === '1'" class="tag2">重要</span>
              </div>
            </th>
            <td>
              <a href="" @click.prevent="() => onClickNewsDetailLink(news)">{{ news.title }}</a>
            </td>
          </tr>
          </tbody>
        </table>
        <div class="text-center" v-if="newsList.length > 0">
          <router-link :to="props.newsListPageUrl" class="com_link1">一覧を見る</router-link>
        </div>
      </div>
      <div class="com_btns2 st1">
        <a v-if="!isNativeApp && (user.orderFlg === 0 || user.orderFlg === 1 || user.orderFlg === 2)" @click="onClickOrderSearch" style="cursor: pointer;">
          <span class="icon1">
            <img src="/front/assets/common/img/icon_b_order1.png" alt="">
          </span>
          <span v-if="user.isPermittedOrder" class="txt1">
            <span class="t1">発注</span>
            <span class="t2">商品の発注/在庫確認はこちらから</span>
          </span>
          <span v-else class="txt1">
            <span class="t1">在庫問合せ</span>
            <span class="t2">商品の在庫確認はこちらから</span>
          </span>
        </a>
        <router-link v-if="!isNativeApp" :to="props.orderResultsPageUrl">
          <span class="icon1">
            <img src="/front/assets/common/img/icon_b_ordered1.png" alt="">
          </span>
          <span class="txt1">
            <span class="t1">注文結果</span>
            <span class="t2">発注商品の状況はこちらから</span>
          </span>
        </router-link>
        <a v-if="!isNativeApp && user.isPermittedReturnRequest" @click="onClickReturnSearch" style="cursor: pointer;">
          <span class="icon1">
            <img src="/front/assets/common/img/icon_b_return1.png" alt="">
          </span>
          <span class="txt1">
            <span class="t1">返品依頼</span>
            <span class="t2">商品の返品はこちらから</span>
          </span>
        </a>
      </div>
    </div>
  </div>
  <div v-if="!isNativeApp && supportLinkList.length > 0" class="com_bg_cnt1">
    <div class="com_inner1">
      <h2 class="com_title2">各種リンク</h2>
      <ul class="com_bn_links1">
        <li v-for="supportLink in supportLinkList">
          <a :href="supportLink.url" target="_blank"><img :src="supportLink.imagePath"></a>
        </li>
      </ul>
    </div>
  </div>

  <ApiErrorModal
    :messages="errorMessages"
  ></ApiErrorModal>

  <Transition>
    <div class="loading-animation" v-show="isLoading">
      <div class="show-icon-box">
        <img :src="props.loadingIcon" />
      </div>
    </div>
  </Transition>
</template>